'use client'

import { useTranslations } from 'next-intl'
import type { PaginationProps } from '@/app/[locale]/_components/Pagination/types'
import AppLink from '@/components/elementary/AppLink'
import type { AppLinkProps } from '@/components/elementary/AppLink/types'
import { ArrowLeft } from '@/components/icons/ArrowLeft'
import { ArrowRight } from '@/components/icons/ArrowRight'
import { cn } from '@/lib/styles'
import { PAGINATION_TEST_IDS } from '@/lib/test-ids'

// this is a client component because we want to be able to use it on both ends, but it cannot use any client-side logic
// adding client-side logic will disable server-side pre-rendering, thus disabling crawling for paginated pages
const PaginationRenderer = ({
  totalPages,
  currentPage = 1,
  useSearchParamPagination,
  pathname,
  paramsObject,
  pagination,
}: PaginationProps) => {
  const t = useTranslations('PaginationRenderer')

  const getPagePath = (pathname: string, page: number) => {
    const path = page === 1 ? pathname : [pathname, 'page', page].join('/')

    // this is a quick and cheap fix to remove duplicate / symbols from the path
    return path.replace(/\/{2,}/g, '/')
  }

  const getPageHref = (page: number, omitWhenFirst = false): AppLinkProps['href'] => {
    if (useSearchParamPagination) {
      return {
        pathname,
        query: {
          ...paramsObject,
          ...(omitWhenFirst && page === 1 ? {} : { page }),
        },
      } as AppLinkProps['href']
    }

    return getPagePath(pathname, page) as AppLinkProps['href']
  }

  if (totalPages <= 1) {
    return null
  }

  return (
    <div
      data-test-id={PAGINATION_TEST_IDS.COMPONENT}
      className="w-full flex justify-center md:justify-between items-center py-8 mt-12 border-t border-neutral-light-500"
    >
      <div className="w-19 justify-start hidden items-center gap-2 text-2xl md:flex">
        {currentPage > 1 && (
          <AppLink
            href={getPageHref(currentPage - 1, true)}
            hrefMode="localized"
            variant="tertiaryLink"
            className="no-underline font-semibold"
            data-test-id={PAGINATION_TEST_IDS.PAGINATION_PREVIOUS}
            iconStart={<ArrowLeft className="size-5" />}
          >
            {t('prev')}
          </AppLink>
        )}
      </div>
      <div className="flex items-end">
        {(pagination ?? []).map((page) => (
          <div key={page}>
            {page === '...' ? (
              <span className="mx-1 text-2xl text-neutral-500">{page}</span>
            ) : (
              <AppLink
                href={getPageHref(Number(page), true)}
                hrefMode="localized"
                size="none"
                className="no-underline justify-center items-center"
                variant="tertiaryLink"
                data-test-id={PAGINATION_TEST_IDS.PAGINATION_PAGE}
              >
                <span
                  className={cn(
                    'size-8 flex items-center justify-center border border-transparent rounded-[5px] text-xl font-bold',
                    currentPage === page && 'border-peach-600 text-peach-600',
                  )}
                >
                  {page}
                </span>
              </AppLink>
            )}
          </div>
        ))}
      </div>
      <div className="w-19 justify-end hidden items-center gap-2 text-2xl  md:flex">
        {currentPage < totalPages && (
          <AppLink
            href={getPageHref(currentPage + 1)}
            hrefMode="localized"
            variant="tertiaryLink"
            className="no-underline font-semibold"
            data-test-id={PAGINATION_TEST_IDS.PAGINATION_NEXT}
            iconEnd={<ArrowRight className="size-5" />}
          >
            {t('next')}
          </AppLink>
        )}
      </div>
    </div>
  )
}

export default PaginationRenderer
